import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Searchable, alpha-sorted dropdown with inline "Crea …" creation.
 *
 * Props:
 *   value             selected id (or "")
 *   onChange(id)      fires after a select OR after a create
 *   options           [{ id, name }]
 *   onCreate(name)    async () => { id, name } — called when the user picks "Crea …"
 *   placeholder       button label when nothing is selected
 *   createLabel       localized "Crea nuovo …" prefix (e.g. "Crea nuova categoria:")
 *   emptyLabel        localized "Nessun risultato"
 *   testId            data-testid prefix
 *   disabled
 *   className
 *   onSelectComplete  optional: called AFTER a value is chosen (from click OR
 *                     from keyboard). Signature: (id) => void. Used to move
 *                     focus to the next form field programmatically. Called
 *                     also after inline creation ("Crea nuovo …").
 *   selectOnEnterIfUnique  default true — when the user presses Enter and
 *                     the current query filters options down to EXACTLY ONE,
 *                     pick that option and close. If multiple results and
 *                     none is explicitly highlighted, do nothing (avoids
 *                     arbitrary choice). Note: cmdk auto-highlights the first
 *                     match, so this covers primarily the "no query typed"
 *                     case and the "0-result" case.
 */
export function SearchableCombobox({
  value,
  onChange,
  options,
  onCreate,
  placeholder = "Seleziona…",
  createLabel = "Crea nuovo:",
  emptyLabel = "Nessun risultato",
  testId = "combobox",
  disabled = false,
  className,
  onSelectComplete,
  selectOnEnterIfUnique = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);

  const sorted = useMemo(
    () =>
      [...(options || [])].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "it", {
          sensitivity: "base",
        }),
      ),
    [options],
  );

  const selected = useMemo(
    () => sorted.find((o) => o.id === value) || null,
    [sorted, value],
  );

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const trimmedQuery = query.trim();
  const lcQuery = trimmedQuery.toLowerCase();
  const exists = sorted.some(
    (o) => (o.name || "").trim().toLowerCase() === lcQuery,
  );
  const canCreate = onCreate && trimmedQuery.length > 0 && !exists;

  // Opzioni che matchano la query (per gestione "seleziona se unica").
  const filtered = useMemo(() => {
    if (!lcQuery) return sorted;
    return sorted.filter((o) =>
      (o.name || "").toLowerCase().includes(lcQuery),
    );
  }, [sorted, lcQuery]);

  const handleSelect = (id) => {
    onChange(id);
    setOpen(false);
    // Sposta il focus al campo successivo (se il consumer lo richiede),
    // dopo che il popover si è chiuso, per non "rubare" focus a Radix.
    if (typeof onSelectComplete === "function") {
      // requestAnimationFrame è sufficiente per attendere il commit del close.
      requestAnimationFrame(() => onSelectComplete(id));
    }
  };

  const handleCreate = async () => {
    if (!canCreate || creating) return;
    setCreating(true);
    try {
      const created = await onCreate(trimmedQuery);
      if (created?.id) {
        onChange(created.id);
        setOpen(false);
        if (typeof onSelectComplete === "function") {
          requestAnimationFrame(() => onSelectComplete(created.id));
        }
      }
    } finally {
      setCreating(false);
    }
  };

  // Handler Enter sul CommandInput. Convenzioni concordate v1.60:
  //   • Enter con esattamente 1 risultato filtrato → seleziona quello.
  //   • Enter con >1 risultati → NON scegliere arbitrariamente (cmdk continua
  //     a evidenziare il primo e lo selezionerà via il proprio path).
  //   • Enter con 0 risultati → NON crea automaticamente (evita entità
  //     spurie da typo). L'utente deve cliccare esplicitamente "Crea nuovo …".
  //   • Enter viene SEMPRE preventDefault-ato per evitare submit involontari
  //     del <form> parent (es. dialog "Nuovo incasso").
  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      // Blocca qualsiasi propagazione: il form del Dialog non deve mai
      // ricevere un submit implicito dalla combobox.
      e.preventDefault();
      e.stopPropagation();
      if (!selectOnEnterIfUnique) return;
      // Caso "1 solo risultato" → seleziona esplicitamente.
      if (filtered.length === 1) {
        handleSelect(filtered[0].id);
        return;
      }
      // Caso "0 risultati" o "molti risultati" → lascia decidere all'utente.
      // Per creare un nuovo record deve cliccare l'item "Crea nuovo …".
    } else if (e.key === "Escape") {
      // Esc chiude senza modificare — comportamento nativo Popover, ma
      // ci assicuriamo che non salga fino al Dialog (che lo chiuderebbe).
      e.stopPropagation();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          data-testid={`${testId}-trigger`}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-stone-500",
            className,
          )}
        >
          <span className="truncate">
            {selected ? selected.name : placeholder}
          </span>
          <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        data-testid={`${testId}-content`}
      >
        <Command shouldFilter>
          <CommandInput
            ref={inputRef}
            placeholder="Cerca…"
            value={query}
            onValueChange={setQuery}
            data-testid={`${testId}-search`}
            onKeyDown={handleInputKeyDown}
          />
          <CommandList>
            <CommandEmpty>
              <div className="px-3 py-2 text-sm text-stone-500">
                {emptyLabel}
              </div>
            </CommandEmpty>
            <CommandGroup>
              {sorted.map((opt) => (
                <CommandItem
                  key={opt.id}
                  value={opt.name}
                  data-testid={`${testId}-item-${opt.id}`}
                  onSelect={() => handleSelect(opt.id)}
                >
                  <Check
                    size={14}
                    className={cn(
                      "mr-2",
                      value === opt.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {opt.name}
                </CommandItem>
              ))}
            </CommandGroup>
            {canCreate && (
              <div className="border-t border-stone-200 p-1">
                <button
                  type="button"
                  data-testid={`${testId}-create`}
                  disabled={creating}
                  onClick={handleCreate}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Plus size={14} className="text-blue-700" />
                  <span>
                    {createLabel}{" "}
                    <strong className="text-stone-900">
                      &ldquo;{trimmedQuery}&rdquo;
                    </strong>
                  </span>
                </button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
